import React, { useState, useEffect, Suspense, useCallback, useRef } from 'react';
import { useSelector, connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Amplify, { Auth } from 'aws-amplify';
import {
  Tabs,
  Tab,
  Tooltip,
  Button,
  Typography,
  Box,
  useTheme,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import BuildIcon from '@mui/icons-material/Build';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyIcon from '@mui/icons-material/Key';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import { isTabVisible } from '../util/featureConfig';
import {
  SECURITY_TEAM_USER,
  USER,
  ADMIN,
  VIEW_ONLY,
  SECURITY,
  OPERATIONAL,
  SUPERVISOR,
} from '../util/constants';
import { dashboardSelector, userTypeSelector, securityGroupSelector } from '../redux/selectors';
import { setSecurityGroup, setDashboard, setUserType } from '../redux/actions/app';
import Loading from '../components/shared/Loading';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import { OPERATIONS_TABS, ADMIN_TABS, SECURITY_TABS } from './Tabs';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import brandIcon from '../assets/Data Centres Primary Horizontal Lock-up.png';
import brandIconLight from '../assets/Data Centres Reversed Horizontal Lock-up.png';
import miniBrandIcon from '../assets/Data Centres Visual Device 216x216px.png';
import LogoutIcon from '@mui/icons-material/Logout';
import FallbackPage from './FallbackPage';
import { BANNER_HEIGHT } from '../components/shared/NewVersionBanner';
import axios from 'axios';
import { validateAuth, getApiBaseUrl } from '../util/util';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import NotificationsWidget from '../components/NotificationsWidget';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Feedback from '../components/Feedback.jsx';
import { fetchToken } from '../firebase';
import { initializeApp } from 'firebase/app';
import { getMessaging, onMessage } from 'firebase/messaging';
import { firebaseConfig } from '../firebaseConfig';
import Help from '../components/Help.jsx';
import HelpIcon from '@mui/icons-material/Help';
import Settings from '../components/Settings/Settings';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import ContactButton from '../components/shared/ContactButton';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ConstructionIcon from '@mui/icons-material/Construction';
import Backdrop from '../components/Backdrop.jsx';
import { canManageAal } from '../util/aalApprovalRules';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  navbar: {
    zIndex: theme.zIndex.drawer + 1,
    position: 'fixed',
    left: 0,
    top: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    width: '72px',
    boxSizing: 'border-box',
    height: '100vh',
    overflow: 'hidden',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    boxShadow: 'none',
    transition: 'width .25s ease-in-out',
  },
  navbarExpanded: {
    padding: theme.spacing(4),
    width: '440px',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  tab: {
    display: 'flex',
    justifyContent: 'start',
  },
  versionInfo: {
    textAlign: 'right',
    textWrap: 'nowrap',
    paddingLeft: '60px',
  },
  routeContainer: {
    position: 'relative',
    display: 'flex',
    maxHeight: '100vh',
    width: '100vw',
  },
  toggleSidebar: {
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const TAB_ICON_STYLE = {
  paddingLeft: '8px',
  width: '22px',
  height: '22px',
};

const SECURITY_GROUPS = {
  '2838edf4-ab8e-4506-9427-9f04a2777e9b': 'SECURITY_TEAM_USER',
  'b55045f7-edda-44a0-9eda-2ecdf573cef5': 'SUPERVISOR',
  '1d321eb9-26cb-40ab-bd9a-5bebb67fb07f': 'USER',
  '641566fd-4da6-44f4-80dd-31e8e9ece322': 'VIEW_ONLY',
  'd738c18f-9ecf-4f49-ae11-4cba2d6e5d31': 'ADMIN',
};

const a11yProps = (index) => ({
  'aria-controls': `vertical-tabpanel-${index}`,
});

const TabLayout = ({
  darkMode,
  setSignOut,
  selectedSecurityGroup,
  setSecurityGroup,
  selectedDashboard,
  setDashboard,
  newVersionAvailable,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const [tabIndex, setTabIndex] = useState(0);
  const signOut = () => Auth.signOut(() => setSignOut()).catch((err) => console.error(err));
  const currentSelectedSecurityGroup = useSelector(securityGroupSelector);
  const currentUserType = useSelector(userTypeSelector);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const handleToggleSidebar = () => setToggleSidebar(!toggleSidebar);
  const sidebarRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const app = initializeApp(firebaseConfig);

  const [isTokenFound, setTokenFound] = useState(false);
  const [notifications, setNotifications] = useState([]);
  let current_notification = {
    id: 0,
    title: '',
    request_type: '',
    ticket_number: '',
    is_unread: true,
    updated_by: '',
    updated_at: new Date(Date.now() - 14000).toISOString(),
    created_at: new Date(Date.now() - 14000).toISOString(),
  };

  const [pastNotifications, setPastNotifications] = useState([]);
  const [permissionReady, setPermissionReady] = useState(false);
  const [refreshNotifications, setRefreshNotifications] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Global 403 interceptor: any API call returning forbidden redirects to /unauthorised
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 403 && location.pathname !== '/unauthorised') {
          window.location.href = '/unauthorised';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const getPastNotifications = () => {
    validateAuth(
      (session) => {
        axios
          .get(`${getApiBaseUrl(currentUserType.type)}/web_push_notifications`, {
            headers: {
              Authorization: session.getIdToken().getJwtToken(),
            },
            timeout: 15 * 1000,
          })
          .then((response) => {
            setPastNotifications(response.data);
            setNotifications([...response.data]);
          });
      },
      (error) => {
        console.error({ error });
      }
    );
  };

  useEffect(() => {
    if (!permissionReady || location.pathname === '/unauthorised') return;

    getPastNotifications();
  }, [permissionReady]);

  useEffect(() => {
    if (refreshNotifications) {
      getPastNotifications();
    }
  }, [refreshNotifications]);

  // To load the token on app load
  useEffect(() => {
    if (!permissionReady) return;
    fetchToken(setTokenFound, currentUserType.type);
  }, [permissionReady]);

  const messaging = getMessaging(app);

  // Foreground messages
  onMessage(messaging, (payload) => {
    current_notification['title'] = payload.notification.title;
    current_notification['request_type'] = payload.data.current_request_type;
    current_notification['ticket_number'] = payload.data.current_ticket_number;
    current_notification['updated_by'] = payload.data.current_updated_by;

    setNotifications([current_notification, ...pastNotifications]);
  });

  // Icons to be mapped to tabs
  const icons = [
    <AccessTimeIcon sx={TAB_ICON_STYLE} />,
    <SecurityIcon sx={TAB_ICON_STYLE} />,
    <BuildIcon sx={TAB_ICON_STYLE} />,
    <PublishedWithChangesIcon sx={TAB_ICON_STYLE} />,
    <WarningAmberIcon sx={TAB_ICON_STYLE} />,
    <KeyIcon sx={TAB_ICON_STYLE} />,
    <EmailRoundedIcon sx={TAB_ICON_STYLE} />,
    <DescriptionIcon sx={TAB_ICON_STYLE} />,
    <SettingsIcon sx={TAB_ICON_STYLE} />,
    <HomeRoundedIcon sx={TAB_ICON_STYLE} />,
    <ConstructionIcon sx={TAB_ICON_STYLE} />,
    <PeopleAltOutlinedIcon sx={TAB_ICON_STYLE} />,
    <BadgeOutlinedIcon sx={TAB_ICON_STYLE} />,
  ];

  const isUnauthorised = location.pathname === '/unauthorised';

  // External users only get Contact Management / AAL Management if they are an
  // Approver or Authorised Access List contact on at least one AAL.
  const isExternalApprover = (currentUserType?.permissions || []).some(canManageAal);

  // Internal visibility for the AAL and Contact Management tabs is security-group based
  const AAL_TAB_GROUPS = [SECURITY_TEAM_USER, SUPERVISOR, USER, ADMIN];
  const CONTACT_TAB_GROUPS = [SECURITY_TEAM_USER, SUPERVISOR, USER, ADMIN];

  const filterInternalRestrictedTabs = (list) =>
    list.filter((tab) => {
      if (tab.type === 'aal') return AAL_TAB_GROUPS.includes(currentSelectedSecurityGroup);
      if (tab.type === 'contact') return CONTACT_TAB_GROUPS.includes(currentSelectedSecurityGroup);
      return true;
    });

  const getTabs = () => {
    const filterByRollout = (list, userType) => list.filter((t) => isTabVisible(t.type, userType));

    if (currentUserType.type === 'external') {
      // Use permission API logic for external users
      // Use the latest values from the store (userType), which are set by setUserType
      return filterByRollout(
        ADMIN_TABS.filter((tab) => {
          const disableMaintDashboard =
            process.env.REACT_APP_FF_DISABLEMAINTDASHFOREXTERNAL_290726 === 'true' &&
            tab.type === 'maintenance';
          const perms = currentUserType && currentUserType.perms;
          if (tab.type === 'aal' || tab.type === 'contact') return isExternalApprover;
          return perms && perms[tab.type] && perms[tab.type] !== 'none' && !disableMaintDashboard;
        }),
        'external'
      );
    } else {
      // Use security group logic for internal users
      if (
        currentSelectedSecurityGroup === 'ADMIN' ||
        currentSelectedSecurityGroup === 'SUPERVISOR'
      ) {
        // ff__disablemaintdash_290626 - feature flag to hide maint dashboard until ready for release
        let tabs = ADMIN_TABS.slice(1);
        if (process.env.REACT_APP_FF_DISABLEMAINTDASH_290626 === 'true') {
          tabs = tabs.filter((tab) => tab.type !== 'maintenance');
        }
        return filterByRollout(filterInternalRestrictedTabs(tabs), 'internal');
      }
      if (currentSelectedSecurityGroup === 'SECURITY_TEAM_USER') {
        return filterByRollout(filterInternalRestrictedTabs(SECURITY_TABS), 'internal');
      }
      return filterByRollout(filterInternalRestrictedTabs(OPERATIONS_TABS), 'internal');
    }
  };

  useEffect(() => {
    if (location.pathname === '/unauthorised') return;
    const fetchUserInformation = async () => {
      try {
        await validateAuth(async (session) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_AWS_API_SHARED_URL}/contacts/permission`,
              {
                headers: {
                  Authorization: session.getIdToken().getJwtToken(),
                  'Content-Type': 'application/json',
                },
                timeout: 45 * 1000,
              }
            );

            const INTERNAL_PERMS = {
              dashboard: 'none',
              access: 'can-raise',
              service: 'can-raise',
              change: 'can-raise',
              security: 'view',
              incidents: 'can-raise',
              keys: 'can-raise',
              notifications: 'can-raise',
              documents: 'can-raise',
              settings: 'internal',
              maintenance: 'view',
            };

            const EXTERNAL_PERMS = {
              dashboard: 'view',
              access: response.data.access,
              service: response.data.service,
              change: response.data.change,
              security: 'none',
              incidents: 'none',
              keys: 'none',
              notifications: 'none',
              documents: 'none',
              settings: 'external',
              maintenance: (response.data.permissions || []).some((p) => p.maintenance)
                ? 'view'
                : 'none',
              aal: (response.data.permissions || []).some(canManageAal) ? 'view' : 'none',
            };

            // If the amplify config does not match the user type, update user type redux
            // store and reload app with the new config
            // Compare both type and perms
            dispatch(
              setUserType({
                ...response.data,
                perms: response.data.type === 'external' ? EXTERNAL_PERMS : INTERNAL_PERMS,
              })
            );
            setPermissionReady(true);

            // Only hard-reload when the user type itself changes (internal ↔ external).
            // Perm-only changes are applied silently via the dispatch above.
            if (currentUserType.type !== response.data.type) {
              window.location.href = '/';
            }
          } catch (error) {
            if (error?.response?.status === 403) {
              window.location.href = '/unauthorised';
              return;
            }
            console.error(error);
            // Fall back to the cached permissions rather than leaving the navigation empty
            setPermissionReady(true);
          }
        });
      } catch (error) {
        // User has logged out, expected error
        if (error.message === 'No current user') {
          return;
        }
        console.error('Error:', error);
      }
    };

    fetchUserInformation();
  }, []);

  // Logic for when the url path changes. Waits for the permission fetch so a first login (or a
  // user whose customer assignments changed) never routes on stale cached permissions.
  useEffect(() => {
    if (!permissionReady) return;
    const tabs = getTabs();
    const index = tabs.findIndex((tab) => location.pathname.includes(tab.pathname));
    // Set the tab index when the route changes.
    if (location.pathname === '/help') {
      setTabIndex('help');
    } else if (location.pathname === '/settings') {
      setTabIndex('settings');
    } else {
      setTabIndex(index === -1 ? 0 : index);
    }

    // Check if user is trying to access a page they do not have perms for
    const permDenied = [...ADMIN_TABS, ...OPERATIONS_TABS, ...SECURITY_TABS].some((tab) => {
      let pathExistsOnSite = tab.pathname === location.pathname;
      const userDoesNotHavePerms = currentUserType.perms[tab.type] === 'none';
      return pathExistsOnSite && userDoesNotHavePerms;
    });

    // User is external with no customer permissions tied to the account,
    // the user also must be trying to access /home or the other request dashboards
    // still let them access settings and help pages.
    const externalUserWithNoCustomers =
      currentUserType &&
      currentUserType.type === 'external' &&
      currentUserType.permissions?.length === 0 &&
      !['/settings', '/help'].includes(location.pathname);

    // If loading onto root, redirect to first valid dashboard
    if (currentSelectedSecurityGroup || currentUserType?.type === 'external') {
      if (externalUserWithNoCustomers) {
        history.push('/nocustomers');
      }
      if (location.pathname === '/' && tabs.length > 0) {
        setTabIndex(0);
        window.location.href = tabs[0].pathname;
      }
      if (permDenied) {
        history.push('/noaccess');
      }

      // TODO: Renable 404 route after URL navigation improvements
      // if (invalidPath) {
      //   window.location.href = '/404';
      // }
    }
  }, [location.pathname, currentSelectedSecurityGroup, permissionReady, currentUserType]);

  const setSecurityTab = useCallback(() => {
    if (selectedDashboard !== SECURITY) setDashboard(SECURITY);
  }, [selectedDashboard, setDashboard]);

  const setOperational = useCallback(() => {
    if (selectedDashboard !== OPERATIONAL) setDashboard(OPERATIONAL);
  }, [selectedDashboard, setDashboard]);

  const setSecurityUserGroup = useCallback(() => {
    if (selectedSecurityGroup !== SECURITY_TEAM_USER) setSecurityGroup(SECURITY_TEAM_USER);
  }, [selectedSecurityGroup, setSecurityGroup]);

  const setAdminGroup = useCallback(() => {
    if (selectedSecurityGroup !== ADMIN) setSecurityGroup(ADMIN);
  }, [selectedSecurityGroup, setSecurityGroup]);

  const setSupervisorGroup = useCallback(() => {
    if (selectedSecurityGroup !== SUPERVISOR) setSecurityGroup(SUPERVISOR);
  }, [selectedSecurityGroup, setSecurityGroup]);

  const setUserGroup = useCallback(() => {
    if (selectedSecurityGroup !== USER) setSecurityGroup(USER);
  }, [selectedSecurityGroup, setSecurityGroup]);

  const setCurrentGroup = useCallback(
    (group) => {
      setSecurityGroup(group);
    },
    [selectedSecurityGroup, setSecurityGroup]
  );

  const setViewOnlyGroup = useCallback(() => {
    if (selectedSecurityGroup !== VIEW_ONLY) setSecurityGroup(VIEW_ONLY);
  }, [selectedSecurityGroup, setSecurityGroup]);

  async function onLoad() {
    try {
      const loggedInUser = await Auth.currentAuthenticatedUser();
      const loggedInUserAttributes = await Auth.userAttributes(loggedInUser);
      const loggedInUserGroups = loggedInUserAttributes.find(
        (attribute) => attribute.Name === 'custom:group'
      )?.Value;

      // For automated e2e testing, default to admin user
      if (window.Cypress) {
        setAdminGroup();
        return;
      }

      if (loggedInUserGroups && loggedInUserGroups.length !== 36) {
        if (loggedInUserGroups.includes('d738c18f-9ecf-4f49-ae11-4cba2d6e5d31')) {
          setAdminGroup();
        } else if (loggedInUserGroups.includes('b55045f7-edda-44a0-9eda-2ecdf573cef5')) {
          setSupervisorGroup();
        } else if (loggedInUserGroups.includes('1d321eb9-26cb-40ab-bd9a-5bebb67fb07f')) {
          setUserGroup();
        } else if (loggedInUserGroups.includes('2838edf4-ab8e-4506-9427-9f04a2777e9b')) {
          setSecurityUserGroup();
        } else {
          setViewOnlyGroup();
        }
      } else {
        setCurrentGroup(SECURITY_GROUPS[loggedInUserGroups]);
      }
    } catch (e) {
      if (e !== 'No current user') {
        console.error('Error', e);
      }
    }
  }

  useEffect(() => {
    onLoad();
  }, []);

  const renderDashboard = () => (
    <>
      <div className={classes.root}>
        {/* Custom Backdrop */}
        <Backdrop
          type={toggleSidebar ? 'dark' : 'light'}
          open={toggleSidebar || showNotifications || showFeedbackForm}
          handleClose={() => {
            setShowNotifications(false);
            setToggleSidebar(false);
            setShowFeedbackForm(false);
          }}
        />
        <div
          className={classes.routeContainer}
          style={{ paddingTop: newVersionAvailable ? BANNER_HEIGHT : 0 }}
        >
          {/* Sidebar component */}
          <Box
            className={`${classes.navbar} ${toggleSidebar && classes.navbarExpanded}`}
            ref={sidebarRef}
            style={{
              top: newVersionAvailable ? BANNER_HEIGHT : 0,
              height: newVersionAvailable ? `calc(100vh - ${BANNER_HEIGHT}px)` : '100vh',
            }}
          >
            <Box
              className="row align-center justify-between width-max"
              sx={{
                flexDirection: toggleSidebar ? 'row' : 'column',
                gap: theme.spacing(2),
              }}
            >
              {toggleSidebar ? (
                <img
                  src={darkMode ? brandIconLight : brandIcon}
                  style={{ maxHeight: toggleSidebar ? '62px' : '40px' }}
                />
              ) : (
                <Tooltip arrow title="Home" placement="right">
                  <Button
                    onClick={() => {
                      // Only push to /home if external user
                      if (currentUserType.type === 'external') {
                        history.push('/home');
                      }
                    }}
                    // TODO: Hook gradients to theme.
                    sx={{
                      backgroundImage:
                        window.location.pathname === '/home' &&
                        `radial-gradient(at 80% 100%, rgba(31, 182, 172, 0.2) 0px, transparent 30%), radial-gradient(at 0% 0%, rgba(0, 43, 254, 0.3) 0px, transparent 40%), linear-gradient(135deg, rgba(0, 43, 254, 0.4) 0%, rgba(31, 182, 172, 0.1) 100%)`,
                      height: '64px',
                    }}
                  >
                    <img
                      src={toggleSidebar ? (darkMode ? brandIconLight : brandIcon) : miniBrandIcon}
                      style={{ maxHeight: toggleSidebar ? '62px' : '40px' }}
                    />
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                arrow
                title={toggleSidebar ? 'Hide Sidebar' : 'Expand Sidebar'}
                placement="right"
              >
                <Button
                  variant="outlined"
                  color="tertiary"
                  aria-label="toggle sidebar"
                  onClick={handleToggleSidebar}
                  className={classes.toggleSidebar}
                  sx={{
                    width: '40px',
                    height: '40px',
                    padding: 1,
                    minWidth: '0',
                    backgroundColor: 'background.default',
                    zIndex: 1002,
                  }}
                >
                  {toggleSidebar ? <MenuOpenIcon /> : <MenuIcon />}
                </Button>
              </Tooltip>
            </Box>

            <Tabs
              orientation="vertical"
              variant="scrollable"
              // Index-based values would point at the wrong tab until the page tabs render
              value={permissionReady ? tabIndex : false}
              aria-label="Navigation Tabs"
              className="col"
              sx={{
                minWidth: '300px',
                minWidth: '300px',
                height: '100%',
                '& .MuiTabs-list': {
                  height: '100%',
                },
              }}
            >
              {!isUnauthorised &&
                permissionReady &&
                getTabs().map(
                  (tab, index) =>
                    !tab.hide && (
                      <Tab
                        iconPosition="start"
                        key={tab.pathname}
                        disabled={!tab.component}
                        disableRipple
                        label={
                          <Typography
                            sx={{
                              whiteSpace: 'nowrap',
                              textTransform: 'capitalize',
                            }}
                          >
                            {tab.label}
                          </Typography>
                        }
                        icon={
                          <Tooltip arrow title={tab.tooltip || tab.label} placement="right">
                            {icons[tab.icon]}
                          </Tooltip>
                        }
                        className={classes.tab}
                        sx={{
                          minHeight: 0,
                          gap: 4,
                          paddingLeft: 0,
                          paddingRight: 0,
                          paddingTop: 1.5,
                          paddingBottom: 1.5,
                          color: `text.contrastText`,
                        }}
                        onClick={() => {
                          history.push({ pathname: tab.pathname });
                          if (tab.label === SECURITY) {
                            setSecurityTab();
                          } else {
                            setOperational();
                          }
                        }}
                        {...a11yProps(index)}
                      />
                    )
                )}
              {!isUnauthorised && currentSelectedSecurityGroup !== 'SECURITY_TEAM_USER' && (
                <Tab
                  iconPosition="start"
                  key="notifications"
                  disableRipple
                  label={
                    <Typography sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                      Notifications
                    </Typography>
                  }
                  icon={
                    <Tooltip arrow title="View Notifications" placement="right">
                      <Badge
                        max={999}
                        color="error"
                        badgeContent={notifications.reduce((count, notification) => {
                          return notification.is_unread ? count + 1 : count;
                        }, 0)}
                      >
                        {showNotifications ? (
                          <NotificationsActiveRoundedIcon sx={TAB_ICON_STYLE} />
                        ) : (
                          <NotificationsNoneRoundedIcon sx={TAB_ICON_STYLE} />
                        )}
                      </Badge>
                    </Tooltip>
                  }
                  className={classes.tab}
                  sx={{
                    marginTop: 'auto',
                    minHeight: 0,
                    gap: 4,
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingTop: 1.5,
                    paddingBottom: 1.5,
                    color: `text.contrastText`,
                  }}
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowFeedbackForm(false);
                  }}
                />
              )}
              {!isUnauthorised && currentUserType.type === 'external' && (
                <Tab
                  iconPosition="start"
                  key="feedback"
                  disableRipple
                  label={
                    <Typography sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                      Send Feedback
                    </Typography>
                  }
                  icon={
                    <Tooltip arrow title="Send Feedback" placement="right">
                      {showFeedbackForm ? (
                        <ChatBubbleRoundedIcon sx={TAB_ICON_STYLE} />
                      ) : (
                        <ChatBubbleOutlineRoundedIcon sx={TAB_ICON_STYLE} />
                      )}
                    </Tooltip>
                  }
                  className={classes.tab}
                  sx={{
                    minHeight: 0,
                    gap: 4,
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingTop: 1.5,
                    paddingBottom: 1.5,
                    color: `text.contrastText`,
                  }}
                  onClick={() => {
                    setShowFeedbackForm(!showFeedbackForm);
                    setShowNotifications(false);
                  }}
                />
              )}
              {!isUnauthorised && currentSelectedSecurityGroup !== 'SECURITY_TEAM_USER' && (
                <Tab
                  iconPosition="start"
                  key="help"
                  value="help"
                  disableRipple
                  label={
                    <Typography sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                      Help
                    </Typography>
                  }
                  icon={
                    <Tooltip arrow title="Help" placement="right">
                      <HelpIcon sx={TAB_ICON_STYLE} />
                    </Tooltip>
                  }
                  className={classes.tab}
                  sx={{
                    minHeight: 0,
                    gap: 4,
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingTop: 1.5,
                    color: `text.contrastText`,
                  }}
                  onClick={() => history.push('/help')}
                />
              )}
              {!isUnauthorised && (
                <Tab
                  iconPosition="start"
                  key="settings"
                  value="settings"
                  disableRipple
                  label={
                    <Typography sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                      Settings
                    </Typography>
                  }
                  icon={
                    <Tooltip arrow title="Settings" placement="right">
                      <SettingsIcon sx={TAB_ICON_STYLE} />
                    </Tooltip>
                  }
                  className={classes.tab}
                  sx={{
                    minHeight: 0,
                    gap: 4,
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingTop: 1.5,
                    paddingBottom: 1.5,
                    color: `text.contrastText`,
                  }}
                  onClick={() => history.push('/settings')}
                />
              )}
              <Tab
                iconPosition="start"
                key="logout"
                disableRipple
                label={
                  <Typography sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                    Log Out
                  </Typography>
                }
                icon={
                  <Tooltip arrow title="Logout" placement="right">
                    <LogoutIcon sx={TAB_ICON_STYLE} />
                  </Tooltip>
                }
                className={classes.tab}
                sx={{
                  minHeight: 0,
                  gap: 4,
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingTop: 1.5,
                  paddingBottom: 1.5,
                  color: `text.contrastText`,
                }}
                onClick={() => {
                  setShowLogoutDialog(true);
                }}
              />
            </Tabs>
          </Box>

          <NotificationsWidget
            data={notifications}
            show={showNotifications}
            type="SIDEBAR"
            sidebarExpanded={toggleSidebar}
            setShowNotifications={setShowNotifications}
            setRefreshNotifications={setRefreshNotifications}
          />

          <Feedback
            show={showFeedbackForm}
            sidebarExpanded={toggleSidebar}
            setShowFeedbackForm={setShowFeedbackForm}
          />
          {!isUnauthorised && currentUserType.type === 'external' && <ContactButton />}

          <Route path="/404">
            <FallbackPage type="error" />
          </Route>

          <Route path="/error">
            <FallbackPage type="error" />
          </Route>

          <Route path="/noaccess">
            <FallbackPage type="noaccess" />
          </Route>

          <Route path="/unauthorised">
            <FallbackPage type="noaccess" />
          </Route>

          <Route path="/nocustomers">
            <FallbackPage type="nocustomers" />
          </Route>

          <Route path="/help">
            <Help />
          </Route>

          <Route path="/settings">
            <Settings />
          </Route>

          {/* Content container */}
          {getTabs().map(
            (tab) =>
              !tab.hide && (
                <Route key={tab.pathname} path={tab.pathname}>
                  <ErrorBoundary fallback={<FallbackPage type="error" />}>
                    <Suspense fallback={<Loading />}>
                      {tab.component ? <tab.component /> : <Redirect to={getTabs()[0].pathname} />}
                    </Suspense>
                  </ErrorBoundary>
                </Route>
              )
          )}
        </div>
        <Dialog open={showLogoutDialog} onClose={() => setShowLogoutDialog(false)}>
          <DialogTitle>Logout?</DialogTitle>
          <DialogContent>Are you sure you want to log out?</DialogContent>
          <DialogActions>
            <Button variant="outlined" disableElevation onClick={() => setShowLogoutDialog(false)}>
              Go Back
            </Button>
            <Button variant="contained" disableElevation onClick={signOut}>
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );

  return renderDashboard();
};

TabLayout.defaultProps = {
  darkMode: false,
  selectedSecurityGroup: null,
  selectedDashboard: null,
  newVersionAvailable: false,
};

TabLayout.propTypes = {
  darkMode: PropTypes.bool,
  setSignOut: PropTypes.func.isRequired,
  setSecurityGroup: PropTypes.func.isRequired,
  selectedSecurityGroup: PropTypes.string,
  setDashboard: PropTypes.func.isRequired,
  selectedDashboard: PropTypes.string,
  newVersionAvailable: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  selectedSecurityGroup: securityGroupSelector(state),
  selectedDashboard: dashboardSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  setSecurityGroup: (event) => dispatch(setSecurityGroup(event)),
  setDashboard: (event) => dispatch(setDashboard(event)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TabLayout);
